import { useState } from 'react';
import { format } from 'date-fns';
import { CreditCard, LogOut, RefreshCw, Trash2 } from 'lucide-react';
import { Input } from '../../components/Field';
import { callApi, openPortal, startCheckout } from '../../lib/api';
import { Button } from '../../components/Button';
import { Card, Page } from '../../components/Page';
import { StatusBadge, type Tone } from '../../components/StatusBadge';
import { signOut, useUser } from '../../auth/session';
import { useAccount } from '../../account/AccountGate';
import { useSync } from '../../sync/SyncProvider';
import { describeSync } from '../../sync/SyncBadge';

const stateLabels: Record<string, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'good' },
  grace: { label: 'Payment problem', tone: 'warn' },
  lapsed: { label: 'Lapsed: read-only', tone: 'danger' },
  none: { label: 'No plan', tone: 'neutral' }
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
      <span className="text-clinical-muted">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

type SignOutStep = 'idle' | 'syncing' | 'confirm' | 'signing-out';

/** Plan actions: manage a yearly subscription, renew a lapsed one, or upgrade (SPEC.md §6.3). */
function PlanActions() {
  const { entitlement } = useAccount();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const row = entitlement.row;
  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : String(actionError));
      setBusy(null);
    }
  };
  if (!row) return null;
  const renewable = row.plan === 'yearly' && (entitlement.state === 'lapsed' || row.status === 'canceled');
  return (
    <div className="mt-3 space-y-2">
      {row.plan === 'lifetime' ? (
        <p className="text-sm text-clinical-muted">Lifetime plan: nothing more to pay. Stripe emailed your receipt when you bought it.</p>
      ) : null}
      {renewable ? (
        <Button className="w-full" disabled={busy !== null} onClick={() => void run('renew', () => startCheckout('yearly'))}>
          {busy === 'renew' ? 'Opening checkout…' : 'Renew yearly plan ($59)'}
        </Button>
      ) : null}
      {row.plan === 'yearly' ? (
        <>
          {!renewable ? (
            <Button variant="secondary" className="w-full" disabled={busy !== null} onClick={() => void run('portal', openPortal)}>
              <CreditCard className="h-4 w-4" /> {busy === 'portal' ? 'Opening…' : 'Manage subscription and payment'}
            </Button>
          ) : null}
          <Button variant="ghost" className="w-full" disabled={busy !== null} onClick={() => void run('lifetime', () => startCheckout('lifetime'))}>
            {busy === 'lifetime' ? 'Opening checkout…' : 'Switch to lifetime ($150 once)'}
          </Button>
          {!renewable ? <p className="text-xs text-clinical-muted">Switching to lifetime stops your yearly renewal; the rest of this year isn’t refunded.</p> : null}
        </>
      ) : null}
      {error ? <p className="text-sm text-clinical-danger">{error}</p> : null}
    </div>
  );
}

/** Account & plan → Delete account (SPEC.md §7.3): type the email to confirm. */
function DeleteAccount({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [state, setState] = useState<'idle' | 'deleting' | { error: string }>('idle');

  async function remove() {
    setState('deleting');
    try {
      await callApi('/account/delete', { email: typed });
      // The account is gone; wipe this device and end the session.
      await signOut().catch(() => undefined);
    } catch (error) {
      setState({ error: error instanceof Error ? error.message : String(error) });
    }
  }

  if (!open) {
    return (
      <Button variant="ghost" className="w-full !text-clinical-danger hover:!bg-red-500/10" onClick={() => setOpen(true)}>
        <Trash2 className="h-4 w-4" /> Delete account
      </Button>
    );
  }
  return (
    <div className="space-y-3 text-sm">
      <p>
        This permanently deletes your account and everything in it from our servers and this device, and cancels any subscription. It can’t be undone. Export a backup first if you want
        a copy.
      </p>
      <label className="block">
        <span className="mb-1 block font-medium">Type {email} to confirm</span>
        <Input value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" autoCapitalize="none" inputMode="email" />
      </label>
      {typeof state === 'object' ? <p className="text-clinical-danger">{state.error}</p> : null}
      <div className="flex gap-2">
        <Button variant="danger" disabled={typed.trim().toLowerCase() !== email.toLowerCase() || state === 'deleting'} onClick={() => void remove()}>
          {state === 'deleting' ? 'Deleting…' : 'Delete everything'}
        </Button>
        <Button variant="secondary" disabled={state === 'deleting'} onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function AccountPage() {
  const user = useUser();
  const { entitlement } = useAccount();
  const sync = useSync();
  const [step, setStep] = useState<SignOutStep>('idle');

  const row = entitlement.row;
  const state = stateLabels[entitlement.state];
  const syncText = sync ? describeSync(sync.status) : null;
  const pending = sync?.status.pending ?? 0;

  async function startSignOut() {
    // Upload anything waiting first, so signing out doesn't lose it.
    setStep('syncing');
    await sync?.syncNow();
    setStep('confirm');
  }

  return (
    <Page title="Account & plan" back="/more">
      <Card title="Account">
        <div className="divide-y divide-clinical-line">
          <Row label="Email">{user.email}</Row>
          <Row label="Sync">{syncText?.label}</Row>
          {syncText?.detail ? <Row label="">{syncText.detail}</Row> : null}
        </div>
        <Button variant="secondary" className="mt-3 w-full" disabled={sync?.status.phase === 'syncing'} onClick={() => void sync?.syncNow()}>
          <RefreshCw className={`h-4 w-4 ${sync?.status.phase === 'syncing' ? 'animate-spin' : ''}`} /> Sync now
        </Button>
      </Card>

      <Card title="Plan">
        <div className="divide-y divide-clinical-line">
          <Row label="Status">
            <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
          </Row>
          <Row label="Plan">{row ? (row.plan === 'lifetime' ? 'Lifetime' : 'Yearly') : 'None'}</Row>
          {row?.plan === 'yearly' && row.current_period_end ? (
            <Row label={row.status === 'canceled' ? 'Ends' : 'Renews'}>{format(new Date(row.current_period_end), 'd MMM yyyy')}</Row>
          ) : null}
          <Row label="Last checked">
            {format(new Date(entitlement.checkedAt), 'd MMM, HH:mm')}
            {entitlement.source === 'cache' ? ' (offline)' : ''}
          </Row>
        </div>
        <PlanActions />
      </Card>

      <Card>
        {step === 'idle' ? (
          <Button variant="secondary" className="w-full" onClick={() => void startSignOut()}>
            <LogOut className="h-4 w-4" /> Sign out of this device
          </Button>
        ) : step === 'syncing' ? (
          <p className="text-sm text-clinical-muted">Saving your latest changes to your account…</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">
              Signing out removes your RenalFlow data from this device.
              {pending ? (
                <strong className="block pt-1 text-clinical-danger">
                  {pending} {pending === 1 ? 'change hasn’t' : 'changes haven’t'} been synced yet and will be lost.
                  {sync?.status.phase === 'offline' ? ' Connect to the internet first to keep them.' : ''}
                </strong>
              ) : (
                ' Everything is saved in your account and comes back when you sign in again.'
              )}
            </p>
            <div className="flex gap-2">
              <Button
                variant="danger"
                disabled={step === 'signing-out'}
                onClick={() => {
                  setStep('signing-out');
                  void signOut();
                }}
              >
                {step === 'signing-out' ? 'Signing out…' : 'Sign out'}
              </Button>
              <Button variant="secondary" disabled={step === 'signing-out'} onClick={() => setStep('idle')}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <DeleteAccount email={user.email ?? ''} />
      </Card>
    </Page>
  );
}
